import { useEffect, useMemo, useState } from 'react'

import { AutocompleteUniselection } from '@yes.technology/react-toolkit'

import useActions from 'modules/interaction/hooks/useActions'
import useSuggestionsFromSources from 'modules/interaction/hooks/useSuggestionsFromSources'

import ActivityWithActions from '../ActivityWithActions/ActivityWithActions'
import useDataSourcesFromFilteraction from 'modules/interaction/hooks/useDataSourcesFromFilteraction'

const getOptions = (options) => {
  if (!Array.isArray(options)) return []

  return options.every(
    ({ des, uuid }) => des !== undefined && uuid !== undefined
  )
    ? options.map(({ des: value, uuid: id }) => ({ id, value }))
    : []
}

const optionToObjects = (option) => {
  if (!option) {
    return []
  }

  return [
    {
      uuid: option.id,
      des: option.des
    }
  ]
}

const EMPTY_ARRAY = []

const ActivityAutocompleteUniselection = ({
  parentPath,
  filteraction,
  filteractionParams,
  onChange,
  value: selectedOption,
  endpoint,
  dataSources: manifestDataSources = EMPTY_ARRAY,
  statusClass,
  isFetchingBlueprints = false,
  actions,
  disabled,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(selectedOption?.des || '')

  const dataSources = useDataSourcesFromFilteraction({
    filteraction,
    filteractionParams,
    manifestDataSources,
    endpoint
  })

  const {
    suggestions,
    setSuggestions,
    isLoading: isLoadingSuggestions,
    debouncedFetchSuggestions,
    resetSuggestions
  } = useSuggestionsFromSources({
    parentPath,
    filteraction,
    filteractionParams,
    endpoint,
    dataSources
  })

  const options = getOptions(suggestions)

  const handleOnChange = (targetSelection) => {
    setInputValue(targetSelection)
    if (targetSelection === '') {
      resetSuggestions()
      onChange('')
      return
    }

    // todo: refactor this to use a uuid instead of a description
    const optionFound = options.find(
      (option) => option.value === targetSelection
    )

    if (!optionFound) {
      debouncedFetchSuggestions(targetSelection)
      return
    }

    onChange({ id: optionFound.id, des: optionFound.value })
  }

  const updateInputValueFromProp = () => {
    if (!selectedOption?.des) {
      return
    }

    setInputValue(selectedOption.des)
  }

  useEffect(updateInputValueFromProp, [selectedOption?.des])

  useEffect(() => {
    setSuggestions([{ des: selectedOption?.des, uuid: selectedOption?.id }])
  }, [selectedOption, setSuggestions])

  const selectedObjects = useMemo(
    () => optionToObjects(selectedOption),
    [selectedOption]
  )

  const { isLoadingFilteraction, onClickAction } = useActions({
    actions,
    filteraction,
    onSelectAndLink: ([value]) => {
      handleOnChange(value.des)

      onChange({
        id: value.uuid,
        des: value.des
      })
    },
    selectedObjects
  })

  const isLoading = isLoadingFilteraction || isLoadingSuggestions

  return (
    <ActivityWithActions
      actions={actions}
      onClickAction={onClickAction}
      uniselection={true}
    >
      <AutocompleteUniselection
        {...props}
        statusClass={isLoading ? 'processing' : statusClass}
        onChange={handleOnChange}
        isLoadingBlueprints={isFetchingBlueprints}
        value={inputValue}
        disabled={disabled}
        autoComplete='new-password'
        {...{ options }}
      />
    </ActivityWithActions>
  )
}

export default ActivityAutocompleteUniselection
